import { GenerateStudyReportJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/GenerateStudyReport/types';
import { MifMarkerUnsupervisedAnalysisJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMifMarkerUnsupervisedAnalysis/types';
import { MultiplexBlobSegmentationJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexBlobSegmentation/types';
import { MultiplexPeakDetectionJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexPeakDetection/types';
import { QaAnalysisJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunQaAnalysis/types';
import { QcAnalysisJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunQcAnalysis/types';
import { NeighborhoodClustersJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/RunNeighborhoodClusters/types';
import { CellQuantificationJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/RunQuantification/types';
import { omit } from 'es-toolkit/compat';
import {
  ModelInference,
  OrchestrationInference,
  PipsExpectedResults,
  ResultNameOrchestrations,
} from 'interfaces/calculateFeatures';
import { ArtifactResultsConfig } from 'interfaces/inferenceArtifacts';
import {
  CalculateFeaturesParams,
  CloudOptimizedSlidesJobConfig,
  CloudOptimizedSlidesSubmissionParams,
  JobNotification,
  JobType,
  SecondaryAnalysisSubmissionParams,
} from 'interfaces/job';
import { CellDetectionDependentJobConfig, MapArtifactToSlides } from 'interfaces/jobs/basic';
import {
  PanelOrchestrationExecuteResponse,
  PanelOrchestrationRequest,
  RnaPanelPlan,
  RnaPanelPlanRequest,
} from 'interfaces/jobs/cellTypingMarkerPositivity';
import { MultiplexCellTypingConfig } from 'interfaces/jobs/multiplex/cellTypingParams';
import { MultiplexHistogramConfig } from 'interfaces/jobs/multiplex/histogramParams';
import { MultiplexNormalizationJobParams } from 'interfaces/jobs/multiplex/normalizationParams';
import { MultiplexThresholdConfig } from 'interfaces/jobs/multiplex/thresholdParams';
import { Model, ModelResponse } from 'interfaces/model';
import { stringify } from 'qs';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';
import { CasesParams } from 'utils/useCasesParams/types';

export interface PlatformDockerImagesResponse {
  images: string[];
  latestVersion?: string | null;
}

export const getImages = (signal?: AbortSignal) => {
  return apiRequestHandlerPromise<PlatformDockerImagesResponse>({
    url: `docker-images.get`,
    method: 'GET',
    signal,
  });
};

export const runInference = (
  data: CasesParams & {
    configParams: any;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.configParams.studyId}/inference.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export interface LaunchCloudOptimizedSlidesResponse {
  job_id: string;
}

export const runCloudOptimizedSlides = (data: {
  studyId: string;
  jobName?: string;
  jobDescription?: string;
  configParams: CloudOptimizedSlidesJobConfig & {
    params?: CloudOptimizedSlidesSubmissionParams;
  };
}) => {
  return apiRequestHandlerPromise<LaunchCloudOptimizedSlidesResponse>({
    url: `studies/${data.studyId}/cloud_optimized_slides.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export interface CloudOptimizedSlidesStatusConfig {
  slideStainType?: string;
  slideIds?: string[];
}

export interface CloudOptimizedSlideStatus {
  slideId: string;
  hasCloudOptimizedSlides: boolean;
  cloudOptimizationVersion?: string | null;
}

export interface CloudOptimizedSlidesStatusResponse {
  slides: CloudOptimizedSlideStatus[];
  missingSlideIds: string[];
  allSlidesHaveCloudOptimizedSlides: boolean;
}

export type InferenceCloudOptimizedSlidesConfig = CloudOptimizedSlidesStatusConfig;
export type InferenceCloudOptimizedSlideStatus = CloudOptimizedSlideStatus;
export type InferenceCloudOptimizedSlidesResponse = CloudOptimizedSlidesStatusResponse;

export const getInferenceCloudOptimizedSlides = (
  data: CasesParams & {
    configParams: InferenceCloudOptimizedSlidesConfig;
  },
  signal?: AbortSignal
) => {
  const studyId = data.studyId || data.filters?.studyId;

  return apiRequestHandlerPromise<InferenceCloudOptimizedSlidesResponse>({
    url: `studies/${studyId}/inference/cloud_optimized_slides.get`,
    method: 'POST',
    signal,
    data: JSON.stringify(data),
  });
};

export const getCloudOptimizedSlidesStatus = (
  data: CasesParams & {
    configParams: CloudOptimizedSlidesStatusConfig;
  },
  signal?: AbortSignal
) => {
  const studyId = data.studyId || data.filters?.studyId;

  return apiRequestHandlerPromise<CloudOptimizedSlidesStatusResponse>({
    url: `studies/${studyId}/cloud_optimized_slides.get`,
    method: 'POST',
    signal,
    data: JSON.stringify(data),
  });
};

export type RunExperimentModelType = 'model_classification' | 'model_areas';

export interface RunExperimentClassConfig {
  id: number;
  dataItems: string[];
}

export interface RunExperimentConfigParams {
  jobName: string;
  jobDescription?: string;
  experimentName?: string;
  trainerDatasetId: string;
  evaluatorDatasetId?: string;
  modelType: RunExperimentModelType;
  classesConfig: Record<string, RunExperimentClassConfig>;
}

export const runExperiment = (data: { studyId: string; configParams: RunExperimentConfigParams }) => {
  return apiRequestHandlerPromise<{ taskId: string }>({
    url: `studies/${data.studyId}/run_experiment.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const getModels = (
  queryParams: {
    modelType: string;
    stainType?: string;
    pageSize?: number;
    page?: number;
    studyId?: string;
    modelId?: string;
    trainingType?: string;
    cancerType?: string;
    sort?: number;
  },
  signal?: AbortSignal
) => {
  return apiRequestHandlerPromise<ModelResponse>({
    url: `models.get?${stringify(queryParams)}`,
    method: 'GET',
    signal,
  });
};

export const getModel = (modelId: string, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<Model>({
    url: `models.get/${modelId}`,
    method: 'GET',
    signal,
  });
};

export const getArtifactsResults = <
  T extends ModelInference | OrchestrationInference | ResultNameOrchestrations = ModelInference
>(
  data: CasesParams & {
    configParams?: ArtifactResultsConfig;
  },
  signal?: AbortSignal
) => {
  return apiRequestHandlerPromise<T[]>({
    url: `studies/${data.studyId}/artifacts/results.get`,
    method: 'POST',
    signal,
    data: JSON.stringify(data),
  });
};

export interface StudyPipelineStatus {
  steps: Array<{
    step: JobType;
    signatureHash: string;
    uniqueParameters: Partial<OrchestrationInference> & {
      step: JobType;
    };
    orchestrationIds: string[];
  }>;
  pipelines: Array<{
    pipelineHash: string;
    upstreamHashes: string[];
    terminalStep: JobType;
    terminalHash: string;
    incomplete: boolean;
  }>;
  pipelineToInstanceOrchestrationSets: {
    [pipelineHash: string]: string[][]; // Map pipeline hash to arrays of orchestration IDs of interconnected steps
  };
  latestTsmArtifacts?: Array<{
    slideId: string;
    experimentResultId: number;
    createdAt?: string;
    orchestrationId?: string;
  }>;
}

export const getStudyPipelineStatus = (data: CasesParams, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<StudyPipelineStatus>({
    url: `studies/${data.studyId}/pipeline_status.get`,
    method: 'POST',
    data: JSON.stringify(data),
    signal,
  });
};

export const getCellTypingMarkerPositivityPlan = ({
  studyId,
  labId,
  panels,
}: {
  studyId: string;
  labId: string;
  panels: RnaPanelPlanRequest[];
}) =>
  apiRequestHandlerPromise<RnaPanelPlan[]>({
    url: `studies/${studyId}/cell_typing_marker_positivity.plan.get`,
    method: 'POST',
    data: JSON.stringify({ labId, panels }),
  });

export const executeCellTypingMarkerPositivityOrchestration = ({
  studyId,
  labId,
  panel,
}: {
  studyId: string;
  labId: string;
  panel: PanelOrchestrationRequest;
}) =>
  apiRequestHandlerPromise<PanelOrchestrationExecuteResponse>({
    url: `studies/${studyId}/cell_typing_marker_positivity.orchestration.execute`,
    method: 'POST',
    data: JSON.stringify({ labId, panel }),
  });

export const executeManualCellTypingMarkerPositivity = ({
  studyId,
  labId,
  sourceJobId,
  panel,
  requestedOutputs,
}: {
  studyId: string;
  labId: string;
  sourceJobId: string;
  panel: RnaPanelPlanRequest;
  requestedOutputs: { positivity: boolean; cellTypes: boolean; hScores: boolean };
}) =>
  apiRequestHandlerPromise({
    url: `studies/${studyId}/cell_typing_marker_positivity.manual.execute`,
    method: 'POST',
    data: JSON.stringify({ labId, sourceJobId, panel, requestedOutputs }),
  });

export const runCalculateFeatures = (data: {
  jobName: string;
  jobDescription: string;
  configParams: CalculateFeaturesParams;
  presetVersionId?: string;
  presetActionOverrides?: {
    postprocessing: Record<string, unknown>[];
    features: Record<string, unknown>[];
  };
}) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.configParams.studyId}/calculate_features.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runSecondaryAnalysis = (
  data: {
    jobName: string;
    jobDescription: string;
    params: SecondaryAnalysisSubmissionParams;
    notifications: JobNotification[];
  },
  signal?: AbortSignal
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.params.studyId}/secondary_analysis.execute`,
    method: 'POST',
    signal,
    data: JSON.stringify(data),
  });
};

export const runMultiplexCellSegmentation = (
  data: CasesParams & {
    configParams: {
      jobName: string;
      jobDescription: string;
      modelType: string;
      nuclearChannelNames: string[];
      membranalChannelNames: string[];
    };
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/multiplex_cell_segmentation.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runMultiplexBinaryClassifier = (
  data: CasesParams & {
    modelPath: string;
    channelNames: string[];
    inputArtifactUrlPatterns: MapArtifactToSlides[];
  }
) => {
  const { modelPath, channelNames, inputArtifactUrlPatterns, ...procedureParams } = data;

  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/multiplex_binary_classifier.execute`,
    method: 'POST',
    data: JSON.stringify({
      ...procedureParams,
      configParams: {
        modelPath,
        channelNames,
        inputArtifactUrlPatterns,
      },
    }),
  });
};

export const runMultiplexTissueSegmentationMask = (
  data: CasesParams & {
    modelPath: string;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/multiplex_tissue_segmentation_mask.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runManualPostIngestPipeline = (data: {
  studyId: string;
  stainFamily: 'brightfield' | 'mIF';
  slideIds: string[];
  dnaChannel?: string;
  jobName?: string;
  jobDescription?: string;
}) => {
  const { studyId, ...requestBody } = data;
  return apiRequestHandlerPromise({
    url: `studies/${studyId}/manual_post_ingest_pipeline.execute`,
    method: 'POST',
    data: JSON.stringify(requestBody),
  });
};

export interface PostIngestPipelineSelectedSlide {
  slideId: string;
  stainingType: string;
  canRunPostIngestPipeline: boolean;
  postIngestPipelineReason?: string | null;
  availableDnaChannels?: string[] | null;
}

export const getManualPostIngestPipelineSlides = (data: CasesParams, signal?: AbortSignal) => {
  const studyId = data.studyId || data.filters?.studyId;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { studyId: _ignoredStudyId, ...requestBody } = data;
  return apiRequestHandlerPromise<PostIngestPipelineSelectedSlide[]>({
    url: `studies/${studyId}/manual_post_ingest_pipeline.slides.get`,
    method: 'POST',
    signal,
    data: JSON.stringify(requestBody),
  });
};

export const runBrightfieldTissueSegmentationMask = (
  data: CasesParams & {
    modelPath: string;
    jobName: string;
    jobDescription?: string;
    // The form includes it for model selection, but the API does not require it.
    // This is to avoid sending it as part of the request by mistake.
    stainType?: string;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/brightfield_tissue_segmentation_mask.execute`,
    method: 'POST',
    data: JSON.stringify(omit(data, 'stainType')),
  });
};

export const runMultiplexHistogram = (
  data: CasesParams & {
    jobName: string;
    jobDescription: string;
    configParams: MultiplexHistogramConfig;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/multiplex_histogram_creation.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runMultiplexNormalization = (
  data: CasesParams & {
    jobName: string;
    jobDescription: string;
    configParams: {
      histogramNormalization: MultiplexNormalizationJobParams;
      histogramCreation: MultiplexHistogramConfig;
    };
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/multiplex_normalization.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runMultiplexThreshold = (
  data: CasesParams & {
    jobName: string;
    jobDescription: string;
    configParams: MultiplexThresholdConfig;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/multiplex_threshold.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runMultiplexCellTyping = (
  data: CasesParams & {
    jobName: string;
    jobDescription: string;
    configParams: MultiplexCellTypingConfig;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/multiplex_cell_typing.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runMultiplexPeakDetection = (
  data: CasesParams & {
    jobName: string;
    jobDescription: string;
    configParams: MultiplexPeakDetectionJobConfig;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/multiplex_peak_detection.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runMultiplexBlobSegmentation = (
  data: CasesParams & {
    jobName: string;
    jobDescription: string;
    configParams: MultiplexBlobSegmentationJobConfig;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/multiplex_blob_segmentation.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runSlideRegistrations = (data: {
  studyId: string;
  jobName: string;
  jobDescription: string;
  slideManifest: { slideId1: string; slideId2: string }[];
}) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/slide_registration.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runCropTma = (data: CasesParams & { detectTmaJobId?: string }) => {
  const { detectTmaJobId, ...requestData } = data;
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/crop_tma.execute${
      detectTmaJobId ? `?detectTmaJobId=${encodeURIComponent(detectTmaJobId)}` : ''
    }`,
    method: 'POST',
    data: JSON.stringify(requestData),
  });
};

export const runDetectTmaCores = (data: CasesParams & { useThresholdedMifTsm: boolean }) => {
  return apiRequestHandlerPromise<{ job_id: string }>({
    url: `studies/${data.studyId}/detect_tma_cores.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runStudyAlgorithm = ({
  jobName,
  jobDescription,
  studyId,
  labId,
  casesParams,
  algorithmId,
}: {
  jobName: string;
  jobDescription?: string;
  studyId: string;
  labId: string;
  casesParams: CasesParams;
  algorithmId: string;
}) => {
  return apiRequestHandlerPromise({
    url: `studies/${studyId}/algorithm.execute/${algorithmId}`,
    method: 'POST',
    data: JSON.stringify({
      ...casesParams,
      jobName,
      jobDescription,
      labId,
      algorithmId,
    }),
  });
};

export const runNeighborhoodClusters = (
  data: CasesParams & {
    jobName: string;
    jobDescription: string;
    configParams: NeighborhoodClustersJobConfig;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/neighborhood_clusters.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runCellSegmentation = (
  data: CasesParams & {
    jobName: string;
    jobDescription: string;
    stainTypeId: string;
    configParams: CellDetectionDependentJobConfig;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/brightfield_cell_segmentation.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runQuantification = (
  data: CasesParams & {
    jobName: string;
    jobDescription: string;
    configParams: CellQuantificationJobConfig;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/cell_quantification.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runQaAnalysis = (
  data: CasesParams & {
    jobName: string;
    jobDescription: string;
    configParams: QaAnalysisJobConfig;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/qa_analysis.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runQcAnalysis = (
  data: CasesParams & {
    jobName: string;
    jobDescription: string;
    configParams: QcAnalysisJobConfig;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/qc_analysis.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runMifMarkerUnsupervisedAnalysis = (
  data: CasesParams & {
    jobName: string;
    jobDescription: string;
    configParams: MifMarkerUnsupervisedAnalysisJobConfig;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/mif_marker_unsupervised_analysis.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const runGenerateStudyReport = (
  data: CasesParams & {
    jobName: string;
    jobDescription: string;
    configParams: GenerateStudyReportJobConfig;
  }
) => {
  return apiRequestHandlerPromise({
    url: `studies/${data.studyId}/study_report.execute`,
    method: 'POST',
    data: JSON.stringify(data),
  });
};

export const getPipsExpectedResults = (
  data: {
    studyId: string;
    orchestrationId: string;
    slideIds: string[];
    stainingType: string;
  },
  signal?: AbortSignal
) => {
  return apiRequestHandlerPromise<PipsExpectedResults>({
    url: `studies/${data.studyId}/calculate_features/expected_results.get`,
    method: 'POST',
    signal,
    data: JSON.stringify(data),
  });
};
